import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import {
  parseCumulativeProductCatalogCountRatingVolumeResponse,
  parseWeeklyProductCatalogCountData,
  useProductCatalogCountRatingData,
  useWeeklyProductCatalogCountData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { EntityCategoryDatum } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' },
  { label: 'Product Catalog Count by Weekly', id: 'weeklyProductCatalogCount' }
];

// For 400 and Above xAxis, we are not able to wrap it with css.
export const CustomXAxisLabel = ({ xAxisCategory }: { xAxisCategory: EntityCategoryDatum }) => {
  //       400         and         Above
  const [firstWord, secondWord, thirdWord] = xAxisCategory?.name?.split(' ');

  return (
    <div
      style={{
        fontSize: '12px',
        textAlign: 'center',
        width: '70px',
        wordBreak: 'break-word',
        maxHeight: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      <p style={{ marginBottom: 0 }} title={xAxisCategory.name}>
        {firstWord}
      </p>
      <p style={{ marginTop: 0 }} title={xAxisCategory.name}>
        {`${secondWord} ${thirdWord}`}
      </p>
    </div>
  );
};

/**
 * Product Catalog Count (by cumulative/ weekly) Rating Volume Chart. This chart can be found on Ratings tab in Review
 *
 */
const ProductCatalogCountRatingVolumeChart = () => {
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const [selectedMetric, setSelectedMetric] = useState('cumulativeProductCatalogCount');
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { data, isLoading: cumulativeLoading } = useProductCatalogCountRatingData(
    RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME
  );
  const { data: weeklyResponse, isLoading: weeklyLoading } = useWeeklyProductCatalogCountData({
    shouldStartFetch: selectedMetric === 'weeklyProductCatalogCount',
    fieldName: RatingsAndReviewMetric.WEEKLY_RATING_VOLUME
  });

  const { compareData, currentData } = data
    ? parseCumulativeProductCatalogCountRatingVolumeResponse({
        data,
        comparisonStartWeekToUse: comparisonStartWeek,
        comparisonEndWeek,
        selectedMetric,
        metricName: 'Ratings'
      })
    : { compareData: [], currentData: [] };

  const { compareData: weeklyCompareData, currentData: weeklyCurrentData } = weeklyResponse
    ? parseWeeklyProductCatalogCountData({ data: weeklyResponse?.data, metricName: 'Ratings' })
    : { compareData: [], currentData: [] };

  return (
    <BeaconChartWithLegend
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(
          selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
          selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
          { metricLabel: 'Product Catalog Count' }
        );
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each rating volume tier
            {selectedMetric === 'cumulativeProductCatalogCount'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: productCatalogCountOptions,
        selectedId: selectedMetric,
        onChange: (option) => setSelectedMetric(option.id),
        suffix: 'Rating Volume Tier'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={cumulativeLoading || weeklyLoading}
      loadingComponent={<SplineChartLoading width={1000} />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          if (xAxisCategory.name === '400 and Above') {
            return ReactDOMServer.renderToStaticMarkup(<CustomXAxisLabel xAxisCategory={xAxisCategory} />);
          }
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ProductCatalogCountRatingVolumeChart;
